import { Trans } from 'react-i18next';
import { SelectorField, TextareaField } from '@anytag/shared-molecules';
import { BaseModal, Form, ModalBody, ModalFooter } from '@anytag/shared-atoms';
import { useQueryHelper, useSelectItem, useFocusTrapReset } from '@anytag/shared-hooks';
import {
  namedOperations,
  MarketplacePostDetail,
  CampaignSocialMediaType,
  useReportMarketplacePostStatusMutation
} from '@anytag/graphql';
import { FormAction } from '@anytag/shared-types';

interface RejectFormValues {
  reason: string;
  detailedReason: string;
}

export const useRejectMarketplacePost = () => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { selectedItem, setSelectedItem, resetSelectedItem } = useSelectItem<MarketplacePostDetail>();

  const { callReportMarketplacePostStatus } = useReportMarketplacePostStatusMutation({
    refetchQueries: [
      namedOperations.Query.AllMarketplacePosts,
      namedOperations.Query.MarketplacePost,
      namedOperations.Query.MarketplacePostHistory
    ]
  });

  const hasAskToEdit =
    !!selectedItem &&
    [CampaignSocialMediaType.YOUTUBE, CampaignSocialMediaType.INSTAGRAM, CampaignSocialMediaType.FACEBOOK].includes(
      selectedItem.socialMedia
    );
  const hasAskToRePost =
    !!selectedItem &&
    [CampaignSocialMediaType.TIKTOK, CampaignSocialMediaType.TWITTER, CampaignSocialMediaType.INSTAGRAM_STORY].includes(
      selectedItem.socialMedia
    );

  const getSocialMediaText = (socialMedia: CampaignSocialMediaType) => {
    switch (socialMedia) {
      case CampaignSocialMediaType.INSTAGRAM:
        return t('Instagram Feed post');
      case CampaignSocialMediaType.INSTAGRAM_STORY:
        return t('Instagram Story post');
      case CampaignSocialMediaType.YOUTUBE:
        return t('YouTube video');
      case CampaignSocialMediaType.DOUYIN:
        return t('Douyin video');
      case CampaignSocialMediaType.TIKTOK:
        return t('TikTok video');
      case CampaignSocialMediaType.TWITTER:
        return t('X(Twitter) post');
      default:
        return '';
    }
  };

  const handleSubmit: FormAction<RejectFormValues>['onSubmit'] = async ({ reason, detailedReason }) => {
    if (!selectedItem?.id) return;

    try {
      resetSelectedItem(); // modal re-render due to parent state update, so better close it first
      await callReportMarketplacePostStatus({
        variables: { input: { id: selectedItem.id, reason, detailedReason } }
      });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };
  useFocusTrapReset();

  return {
    handleRejectPost: setSelectedItem,
    RejectMarketplacePostModal: () => (
      <BaseModal
        open={!!selectedItem?.id}
        onClose={resetSelectedItem}
        title={t('Dialog.Report Inappropriate')}
        description={
          hasAskToEdit ? (
            <>
              {getSocialMediaText(selectedItem.socialMedia)}&nbsp;
              <Trans i18nKey="Dialog.ReportInappropriateEditDescription" components={{ b: <b /> }} />
            </>
          ) : hasAskToRePost ? (
            <Trans i18nKey="Dialog.ReportInappropriateRePostDescription" components={{ b: <b /> }} />
          ) : undefined
        }
        asChild
      >
        <Form.Provider<RejectFormValues>
          onSubmit={handleSubmit}
          defaultValues={{ reason: selectedItem?.reason || '', detailedReason: selectedItem?.detailedReason || '' }}
        >
          {({ values, formState }) => (
            <>
              <ModalBody>
                <SelectorField<keyof RejectFormValues>
                  name="reason"
                  title={t('Selector.Reason')}
                  options={[
                    { label: t<string>('Option.Inappropriate contents'), value: 'Inappropriate contents' },
                    {
                      label: t<string>('Option.Doesn’t match the conditions'),
                      value: 'Doesn’t match the conditions'
                    }
                  ]}
                  required
                />

                <TextareaField<keyof RejectFormValues>
                  name="detailedReason"
                  title={t('TextForm.Detailed Reason')}
                  help={t('Tooltip.Detailed Reason Help')}
                  placeholder={t<string>('TextForm.Detailed Reason Placeholder')}
                  required
                />
              </ModalBody>

              <ModalFooter
                cancelButton={{ onClick: resetSelectedItem }}
                submitButton={{
                  type: 'submit',
                  title: t('Button.Send'),
                  loading: formState.isSubmitting,
                  disabled: !values.reason || !values.detailedReason
                }}
              />
            </>
          )}
        </Form.Provider>
      </BaseModal>
    )
  };
};
